/**
 * DamyxIA Professional Interface Components
 * Sistema de sincronización avanzado entre paneles
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  Zap, CheckCircle2, Clock, Code, Image, GitBranch, 
  Loader2, Eye, Bell, Activity, Wrench, Lightbulb,
  ArrowRight, ChevronDown, ChevronUp, Sparkles,
  Search, Palette, Type, MousePointer, Layers,
  MessageSquare, X, Copy, Check, Download, Trash2, Save,
} from 'lucide-react';

// Traducciones para WorkspaceSync
const workspaceTranslations = {
  es: {
    workflowTitle: 'Flujo de Trabajo',
    processing: 'Procesando...',
    analysis: 'Análisis',
    analysisDesc: 'Analizando tu solicitud',
    plan: 'Plan',
    planDesc: 'Creando plan de acción',
    execution: 'Ejecución',
    executionDesc: 'Generando solución',
    review: 'Revisión',
    reviewDesc: 'Verificando calidad',
    repair: 'Reparación',
    repairDesc: 'Aplicando correcciones',
    closeConversation: 'Cerrar conversación',
    copy: 'Copiar',
    save: 'Guardar',
    delete: 'Eliminar',
    noConversations: 'Sin conversaciones',
    conversations: 'Conversaciones',
    sessionMetrics: 'Métricas de Sesión',
    components: 'Componentes',
    fixes: 'Correcciones',
    lines: 'Líneas',
    time: 'Tiempo',
    recentActions: 'Acciones Recientes',
    seeMore: 'más',
    seeLess: 'Ver menos',
    changesApplied: 'Cambios Aplicados',
    inspectionMode: 'Modo Inspección',
    active: 'Activo',
    activate: 'Activar',
    color: 'Color',
    typography: 'Tipografía',
    component: 'Componente',
    state: 'Estado',
    default: 'Predeterminado',
    syncedWithLeftPanel: 'Sincronizado con Panel Izquierdo',
    syncingChanges: 'Sincronizando cambios...',
    lastUpdate: 'Última actualización',
    realTimeView: 'Vista en tiempo real',
    pending: 'pendientes',
    workspace: 'Área de Trabajo',
    liveViewExecution: 'Vista en vivo y ejecución',
    synced: 'Sincronizado',
    rightPanel: 'Panel Derecho',
    dragButton: 'Arrastra el botón',
    newConversation: 'Nueva conversación',
  },
  en: {
    workflowTitle: 'DamyxIA Workflow',
    processing: 'Processing...',
    analysis: 'Analysis',
    analysisDesc: 'Analyzing your request',
    plan: 'Plan',
    planDesc: 'Creating action plan',
    execution: 'Execution',
    executionDesc: 'Generating solution',
    review: 'Review',
    reviewDesc: 'Verifying quality',
    repair: 'Repair',
    repairDesc: 'Applying fixes',
    closeConversation: 'Close conversation',
    copy: 'Copy',
    save: 'Save',
    delete: 'Delete',
    noConversations: 'No conversations',
    conversations: 'Conversations',
    sessionMetrics: 'Session Metrics',
    components: 'Components',
    fixes: 'Fixes',
    lines: 'Lines',
    time: 'Time',
    recentActions: 'Recent Actions',
    seeMore: 'more',
    seeLess: 'See less',
    changesApplied: 'Changes Applied',
    inspectionMode: 'Inspection Mode',
    active: 'Active',
    activate: 'Activate',
    color: 'Color',
    typography: 'Typography',
    component: 'Component',
    state: 'State',
    default: 'Default',
    syncedWithLeftPanel: 'Synced with Left Panel',
    syncingChanges: 'Syncing changes...',
    lastUpdate: 'Last update',
    realTimeView: 'Real-time view',
    pending: 'pending',
    workspace: 'Workspace',
    liveViewExecution: 'Live view and execution',
    synced: 'Synced',
    rightPanel: 'Right Panel',
    dragButton: 'Drag the button',
    newConversation: 'New conversation',
  }
};

// =====================================================
// WORKFLOW DE 5 PASOS - Panel Izquierdo
// =====================================================
export const WorkflowPanel = ({ currentStep = 0, isProcessing = false, stepDetails = null }) => {
  const { language } = useLanguage();
  const t = workspaceTranslations[language] || workspaceTranslations.es;
  
  const steps = [
    { id: 1, name: t.analysis, icon: Search, description: t.analysisDesc, color: 'blue' },
    { id: 2, name: t.plan, icon: Lightbulb, description: t.planDesc, color: 'yellow' },
    { id: 3, name: t.execution, icon: Code, description: t.executionDesc, color: 'emerald' },
    { id: 4, name: t.review, icon: Eye, description: t.reviewDesc, color: 'purple' },
    { id: 5, name: t.repair, icon: Wrench, description: t.repairDesc, color: 'orange' },
  ];

  const getStepColor = (stepId, color) => {
    if (currentStep > stepId) return 'bg-green-500 text-white border-green-500';
    if (currentStep === stepId) return `bg-${color}-500 text-white border-${color}-500 ring-4 ring-${color}-500/30`;
    return 'bg-slate-800 text-slate-500 border-slate-700';
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-lg p-2 mb-2">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 bg-indigo-500/20 rounded flex items-center justify-center">
            <Zap className="w-3 h-3 text-indigo-400" />
          </div>
          <h3 className="text-[10px] font-semibold text-white">{t.workflowTitle}</h3>
        </div>
        {isProcessing && (
          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-500/20 rounded-full">
            <Loader2 className="w-2.5 h-2.5 text-blue-400 animate-spin" />
            <span className="text-[8px] text-blue-400">{t.processing}</span>
          </div>
        )}
      </div>

      {/* Steps Progress - More compact */}
      <div className="flex items-center justify-between">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          const isPending = currentStep < step.id;
          
          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div 
                  className={`w-6 h-6 rounded-full flex items-center justify-center border transition-all duration-300 ${
                    isCompleted 
                      ? 'bg-green-500 text-white border-green-500' 
                      : isCurrent 
                        ? 'bg-indigo-500 text-white border-indigo-500 ring-2 ring-indigo-500/30 animate-pulse' 
                        : 'bg-slate-800 text-slate-500 border-slate-700'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : (
                    <Icon className="w-3 h-3" />
                  )}
                </div>
                <span className={`text-[7px] mt-0.5 font-medium ${
                  isCompleted ? 'text-green-400' : isCurrent ? 'text-indigo-400' : 'text-slate-500'
                }`}>
                  {step.name}
                </span>
              </div>
              
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 transition-all duration-500 ${
                  currentStep > step.id ? 'bg-green-500' : 'bg-slate-700'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// =====================================================
// MÉTRICAS DE PRODUCTIVIDAD - Panel Izquierdo (Compacto)
// =====================================================
export const ProductivityPanel = ({ metrics = {} }) => {
  const { language } = useLanguage();
  const t = workspaceTranslations[language] || workspaceTranslations.es;
  
  const stats = {
    components: metrics.components || 0,
    fixes: metrics.fixes || 0,
    lines: metrics.lines || 0,
    time: metrics.time || '0s',
  };

  return (
    <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-2 mb-2">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Activity className="w-3 h-3 text-emerald-400" />
        <span className="text-[10px] font-medium text-white">{t.sessionMetrics}</span>
      </div>
      
      <div className="grid grid-cols-4 gap-1">
        <div className="bg-slate-800/50 rounded p-1.5 text-center">
          <div className="text-sm font-bold text-emerald-400">{stats.components}</div>
          <div className="text-[7px] text-slate-400">{t.components}</div>
        </div>
        <div className="bg-slate-800/50 rounded p-1.5 text-center">
          <div className="text-sm font-bold text-green-400">{stats.fixes}</div>
          <div className="text-[7px] text-slate-400">{t.fixes}</div>
        </div>
        <div className="bg-slate-800/50 rounded p-1.5 text-center">
          <div className="text-sm font-bold text-cyan-400">{stats.lines}</div>
          <div className="text-[7px] text-slate-400">{t.lines}</div>
        </div>
        <div className="bg-slate-800/50 rounded p-1.5 text-center">
          <div className="text-sm font-bold text-purple-400">{stats.time}</div>
          <div className="text-[7px] text-slate-400">{t.time}</div>
        </div>
      </div>
    </div>
  );
};

// =====================================================
// HISTORIAL DE ACCIONES - Panel Izquierdo
// =====================================================
export const ActionsHistory = ({ actions = [], maxItems = 5 }) => {
  const { language } = useLanguage();
  const t = workspaceTranslations[language] || workspaceTranslations.es;
  const [expanded, setExpanded] = useState(false);
  const displayActions = expanded ? actions : actions.slice(0, maxItems);

  const getActionIcon = (type) => {
    const icons = {
      code: Code,
      fix: Wrench,
      analysis: Search,
      preview: Eye,
      image: Image,
    };
    return icons[type] || Zap;
  };

  const getActionColor = (type) => {
    const colors = {
      code: 'text-emerald-400 bg-emerald-500/20',
      fix: 'text-orange-400 bg-orange-500/20',
      analysis: 'text-blue-400 bg-blue-500/20',
      preview: 'text-purple-400 bg-purple-500/20',
      image: 'text-pink-400 bg-pink-500/20',
    };
    return colors[type] || 'text-slate-400 bg-slate-500/20';
  };

  if (actions.length === 0) return null;

  return (
    <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-3 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-medium text-white">{t.recentActions}</span>
        </div>
        {actions.length > maxItems && (
          <button 
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
          >
            {expanded ? t.seeLess : `+${actions.length - maxItems} ${t.seeMore}`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>
      
      <div className="space-y-2">
        {displayActions.map((action, idx) => {
          const Icon = getActionIcon(action.type);
          const colorClass = getActionColor(action.type);
          
          return (
            <div 
              key={idx} 
              className="flex items-center gap-2 p-2 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors"
            >
              <div className={`w-6 h-6 rounded-md flex items-center justify-center ${colorClass}`}>
                <Icon className="w-3 h-3" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-white truncate">{action.title}</p>
                <p className="text-[9px] text-slate-500">{action.time}</p>
              </div>
              {action.synced && (
                <CheckCircle2 className="w-3 h-3 text-green-400" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =====================================================
// NOTIFICACIONES DE CAMBIOS - Panel Izquierdo
// =====================================================
export const ChangeNotifications = ({ notifications = [], onDismiss }) => {
  const { language } = useLanguage();
  const t = workspaceTranslations[language] || workspaceTranslations.es;
  
  if (notifications.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-500/30 rounded-xl p-3 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Bell className="w-4 h-4 text-indigo-400" />
        <span className="text-xs font-medium text-white">{t.changesApplied}</span>
        <span className="text-[9px] bg-indigo-500/30 text-indigo-300 px-1.5 py-0.5 rounded-full">
          {notifications.length}
        </span>
      </div>
      
      <div className="space-y-1.5">
        {notifications.slice(0, 3).map((notif, idx) => (
          <div 
            key={idx}
            className="flex items-center gap-2 p-2 bg-slate-800/30 rounded-lg text-[11px]"
          >
            <ArrowRight className="w-3 h-3 text-indigo-400" />
            <span className="text-slate-300 flex-1">{notif.message}</span>
            <span className="text-slate-500 text-[9px]">{notif.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// =====================================================
// PANEL DE INSPECCIÓN VISUAL - Panel Derecho
// =====================================================
export const InspectionMode = ({ isActive, onToggle, inspectionData }) => {
  const { language } = useLanguage();
  const t = workspaceTranslations[language] || workspaceTranslations.es;
  
  return (
    <div className={`border rounded-xl p-3 mb-4 transition-all ${
      isActive 
        ? 'bg-purple-900/30 border-purple-500/50' 
        : 'bg-slate-800/30 border-slate-700/50'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MousePointer className="w-4 h-4 text-purple-400" />
          <span className="text-xs font-medium text-white">{t.inspectionMode}</span>
        </div>
        <button
          onClick={onToggle}
          className={`px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${
            isActive 
              ? 'bg-purple-500 text-white' 
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          {isActive ? t.active : t.activate}
        </button>
      </div>
      
      {isActive && inspectionData && (
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-slate-800/50 rounded-lg p-2">
            <div className="flex items-center gap-1.5 mb-1">
              <Palette className="w-3 h-3 text-pink-400" />
              <span className="text-[9px] text-slate-400">{t.color}</span>
            </div>
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded border border-slate-600" 
                style={{ backgroundColor: inspectionData.color || '#3B82F6' }}
              />
              <span className="text-[10px] text-white font-mono">
                {inspectionData.color || '#3B82F6'}
              </span>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-2">
            <div className="flex items-center gap-1.5 mb-1">
              <Type className="w-3 h-3 text-cyan-400" />
              <span className="text-[9px] text-slate-400">{t.typography}</span>
            </div>
            <span className="text-[10px] text-white">
              {inspectionData.font || 'Inter, 14px'}
            </span>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-2">
            <div className="flex items-center gap-1.5 mb-1">
              <Layers className="w-3 h-3 text-emerald-400" />
              <span className="text-[9px] text-slate-400">{t.component}</span>
            </div>
            <span className="text-[10px] text-white">
              {inspectionData.component || 'Button'}
            </span>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-2">
            <div className="flex items-center gap-1.5 mb-1">
              <Eye className="w-3 h-3 text-yellow-400" />
              <span className="text-[9px] text-slate-400">{t.state}</span>
            </div>
            <span className="text-[10px] text-white">
              {inspectionData.state || t.default}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================
// BANNER DE SINCRONIZACIÓN - Panel Derecho
// =====================================================
export const SyncStatusBanner = ({ isSynced, lastUpdate, pendingChanges = 0 }) => {
  const { language } = useLanguage();
  const t = workspaceTranslations[language] || workspaceTranslations.es;
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-xl mb-4 ${
      isSynced 
        ? 'bg-green-900/20 border border-green-500/30' 
        : 'bg-yellow-900/20 border border-yellow-500/30'
    }`}>
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isSynced ? 'bg-green-500/20' : 'bg-yellow-500/20'
        }`}>
          {isSynced ? (
            <CheckCircle2 className="w-4 h-4 text-green-400" />
          ) : (
            <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
          )}
        </div>
        <div>
          <p className={`text-xs font-medium ${isSynced ? 'text-green-400' : 'text-yellow-400'}`}>
            {isSynced ? t.syncedWithLeftPanel : t.syncingChanges}
          </p>
          <p className="text-[10px] text-slate-400">
            {lastUpdate ? `${t.lastUpdate}: ${lastUpdate}` : t.realTimeView}
          </p>
        </div>
      </div>
      
      {pendingChanges > 0 && (
        <div className="px-2 py-1 bg-yellow-500/20 rounded-full">
          <span className="text-[10px] text-yellow-400">{pendingChanges} {t.pending}</span>
        </div>
      )}
    </div>
  );
};

// =====================================================
// HEADER DEL PANEL IZQUIERDO - DamyxIA
// =====================================================
export const MindPanelHeader = ({ 
  isProcessing, 
  onCloseConversation,
  onRefreshConversation,
  onExpandPanel,
  isExpanded = false,
  onOpenHomepage,
  // Props para IdeaLightbulb
  onOpenGroupsProjects,
  onOpenServicesInfo,
  onOpenStepPanel,
  onAttachFile,
  onOpenImageGenerator,
  onOpenDiagramGenerator,
  onInstallApp,
  onOpenImageGallery
}) => {
  // Import IdeaLightbulb directly
  const IdeaLightbulb = React.lazy(() => import('./IdeaLightbulb'));
  const { language } = useLanguage();
  const t = workspaceTranslations[language] || workspaceTranslations.es;

  return (
    <div className="bg-gradient-to-r from-slate-900 to-slate-800 border-b border-slate-700 px-4 pt-[23px] pb-[5px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Logo DamyxIA con sol girando - pequeño, proporcional al texto */}
          <div className="relative w-6 h-6 flex-shrink-0 transition-transform duration-300 hover:scale-150 cursor-pointer">
            {/* Rayos de sol girando */}
            <div className="absolute inset-0 animate-spin" style={{ animationDuration: '20s' }}>
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="absolute top-1/2 left-1/2 w-0.5 h-full origin-bottom opacity-40"
                  style={{
                    background: 'linear-gradient(to top, rgba(255, 215, 0, 0.8), transparent)',
                    transform: `translate(-50%, -100%) rotate(${i * 45}deg)`,
                    filter: 'blur(0.5px)'
                  }}
                />
              ))}
            </div>
            {/* Imagen */}
            <div 
              className="w-6 h-6 rounded-full overflow-hidden relative"
              style={{ boxShadow: '0 0 12px rgba(255, 215, 0, 0.4), 0 0 20px rgba(99, 102, 241, 0.3)' }}
            >
              <img 
                src="/icons/icon-192x192.png" 
                alt="DamyxIA" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <h2 className="text-sm font-semibold bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
            DamyxIA
          </h2>
          
          {/* Botón Homepage (casita) - al lado derecho de DamyxIA */}
          {onOpenHomepage && (
            <button
              onClick={onOpenHomepage}
              className="p-1.5 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20 rounded-lg transition-all hover:scale-110"
              title={language === 'es' ? 'Guía de DamyxIA' : 'DamyxIA Guide'}
              data-testid="homepage-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          )}
          
          {/* Bombillo/IdeaLightbulb al lado de DamyxIA */}
          <React.Suspense fallback={<div className="w-10 h-10" />}>
            <IdeaLightbulb 
              onOpenGroupsProjects={onOpenGroupsProjects}
              onOpenServicesInfo={onOpenServicesInfo}
              onOpenStepPanel={onOpenStepPanel}
              onAttachFile={onAttachFile}
              onOpenImageGenerator={onOpenImageGenerator}
              onOpenDiagramGenerator={onOpenDiagramGenerator}
              onInstallApp={onInstallApp}
              onOpenImageGallery={onOpenImageGallery}
            />
          </React.Suspense>
        </div>
        
        {/* Controles a la derecha: Refresh, X (cerrar), Expand */}
        <div className="flex items-center gap-1.5">
          {isProcessing && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-500/20 border border-indigo-500/30 rounded-full mr-2">
              <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" />
              <span className="text-[10px] text-indigo-300 font-medium">
                {language === 'es' ? 'Procesando' : 'Processing'}
              </span>
            </div>
          )}
          
          {/* Botón Refrescar */}
          {onRefreshConversation && (
            <button
              onClick={onRefreshConversation}
              className="p-1.5 text-zinc-400 hover:text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition-colors"
              title={language === 'es' ? 'Refrescar conversación' : 'Refresh conversation'}
              data-testid="refresh-conversation-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
          
          {/* Botón X para cerrar/borrar conversación */}
          {onCloseConversation && (
            <button
              onClick={onCloseConversation}
              className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
              title={t.closeConversation}
              data-testid="close-conversation-x-btn"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          
          {/* Botón Expandir/Contraer panel */}
          {onExpandPanel && (
            <button
              onClick={onExpandPanel}
              className="p-1.5 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-colors"
              title={isExpanded 
                ? (language === 'es' ? 'Contraer panel' : 'Collapse panel')
                : (language === 'es' ? 'Expandir a pantalla completa' : 'Expand to fullscreen')
              }
              data-testid="expand-panel-btn"
            >
              {isExpanded ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// =====================================================
// BOTÓN FLOTANTE DE CONVERSACIONES (Draggable)
// =====================================================
export const FloatingConversationsButton = ({ 
  conversations = [], 
  currentConversation,
  onSelect,
  onDelete,
  onClose,
  onCopy,
  onExport,
  externalOpen = false,
  onExternalOpenChange
}) => {
  const { language } = useLanguage();
  const t = workspaceTranslations[language] || workspaceTranslations.es;
  const [isOpen, setIsOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  // Sync with external open state
  useEffect(() => {
    if (externalOpen && !isOpen) {
      setIsOpen(true);
      if (onExternalOpenChange) onExternalOpenChange(false);
    }
  }, [externalOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Draggable state
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('conversationsBtnPosition');
    return saved ? JSON.parse(saved) : { x: 16, y: window.innerHeight - 120 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Handle drag
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newX = Math.max(10, Math.min(window.innerWidth - 40, e.clientX - dragOffset.x));
      const newY = Math.max(64, Math.min(window.innerHeight - 40, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    };
    
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        localStorage.setItem('conversationsBtnPosition', JSON.stringify(position));
      }
    };
    
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, position]);

  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleClick = () => {
    if (!isDragging) {
      setIsOpen(!isOpen);
    }
  };

  const handleCopy = async (conv, e) => {
    e.stopPropagation();
    if (onCopy) {
      await onCopy(conv);
      setCopiedId(conv.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  return (
    <>
      {/* Floating Button - Small and Draggable */}
      <button
        onMouseDown={handleDragStart}
        onClick={handleClick}
        className={`fixed z-50 w-8 h-8 rounded-lg shadow-lg transition-all duration-200 cursor-grab active:cursor-grabbing ${
          isDragging ? 'scale-110 opacity-80' : 'hover:scale-105'
        } ${isOpen ? 'bg-indigo-600' : 'bg-gradient-to-br from-indigo-600 to-purple-600'}`}
        style={{ 
          left: position.x, 
          top: position.y,
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
        }}
        data-testid="floating-conversations-btn"
      >
        <div className="w-full h-full flex items-center justify-center relative">
          {isOpen ? (
            <X className="w-4 h-4 text-white" />
          ) : (
            <>
              <MessageSquare className="w-4 h-4 text-white" />
              {conversations.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                  {conversations.length > 99 ? '99' : conversations.length}
                </span>
              )}
            </>
          )}
        </div>
      </button>

      {/* Floating Window */}
      {isOpen && (
        <div 
          className="fixed z-50 w-72 max-h-80 rounded-xl overflow-hidden shadow-2xl"
          style={{ 
            left: position.x,
            top: position.y - 320,
            backgroundColor: '#0F172A',
            border: '1px solid #334155',
            boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Window Header */}
          <div 
            className="px-3 py-2 border-b flex items-center justify-between"
            style={{ backgroundColor: '#1E293B', borderColor: '#334155' }}
          >
            <div className="flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5 text-indigo-400" />
              <span className="text-xs font-semibold text-white">Conversaciones</span>
              <span 
                className="text-[9px] px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: '#3B82F620', color: '#3B82F6' }}
              >
                {conversations.length}
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-slate-700 transition-colors"
            >
              <X className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>

          {/* Conversations List */}
          <div className="max-h-56 overflow-y-auto p-1.5 space-y-1">
            {conversations.length === 0 ? (
              <div className="py-6 text-center">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                <p className="text-xs text-slate-400">{t.noConversations}</p>
              </div>
            ) : (
              conversations.map((conv, index) => {
                const isActive = currentConversation?.id === conv.id;
                const preview = conv.messages?.[0]?.content?.slice(0, 40) || 'Nueva conversación';
                
                return (
                  <div
                    key={conv.id}
                    onClick={() => {
                      onSelect(conv.id);
                      setIsOpen(false);
                    }}
                    className={`p-2 rounded-lg cursor-pointer transition-all group ${
                      isActive 
                        ? 'bg-indigo-600/20 border border-indigo-500/40' 
                        : 'hover:bg-slate-800/80 border border-transparent'
                    }`}
                    style={{ backgroundColor: isActive ? undefined : '#1E293B' }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span 
                            className="text-[9px] font-bold px-1 py-0.5 rounded"
                            style={{ backgroundColor: '#334155', color: '#94A3B8' }}
                          >
                            #{index + 1}
                          </span>
                          {isActive && (
                            <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1 truncate">
                          {preview}...
                        </p>
                      </div>
                      
                      {/* Action Buttons - Copiar, Salvar, Borrar */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {/* Copiar */}
                        <button
                          onClick={(e) => handleCopy(conv, e)}
                          className="p-1.5 rounded-md bg-slate-700/50 hover:bg-slate-600 transition-colors"
                          title={t.copy}
                        >
                          {copiedId === conv.id ? (
                            <Check className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3 text-slate-300" />
                          )}
                        </button>
                        {/* Salvar */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onExport) onExport(conv);
                          }}
                          className="p-1.5 rounded-md bg-green-600/30 hover:bg-green-600/50 transition-colors"
                          title={t.save}
                        >
                          <Save className="w-3 h-3 text-green-400" />
                        </button>
                        {/* Borrar */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onDelete) onDelete(conv.id);
                          }}
                          className="p-1.5 rounded-md bg-red-600/20 hover:bg-red-600/40 transition-colors"
                          title={t.delete}
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Window Footer */}
          <div 
            className="px-3 py-1.5 border-t text-center"
            style={{ backgroundColor: '#1E293B', borderColor: '#334155' }}
          >
            <p className="text-[8px] text-slate-500">
              {t.dragButton} • DamyxIA
            </p>
          </div>
        </div>
      )}
    </>
  );
};

// =====================================================
// HEADER DEL PANEL DERECHO - "Manos"
// =====================================================
export const WorkspacePanelHeader = ({ isSynced, hasContent }) => {
  const { language } = useLanguage();
  const t = workspaceTranslations[language] || workspaceTranslations.es;
  
  return (
    <div className="bg-gradient-to-r from-emerald-900/50 to-cyan-900/50 border-b border-emerald-500/30 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
            <Eye className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-white">{t.workspace}</h2>
            <p className="text-[10px] text-emerald-200">{t.liveViewExecution}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSynced && hasContent && (
            <div className="flex items-center gap-1.5 px-2 py-1 bg-green-500/20 rounded-full">
              <CheckCircle2 className="w-3 h-3 text-green-400" />
              <span className="text-[10px] text-green-300">{t.synced}</span>
            </div>
          )}
          <span className="text-[9px] text-slate-400 bg-slate-800/50 px-2 py-1 rounded-full">
            {t.rightPanel}
          </span>
        </div>
      </div>
    </div>
  );
};

export default {
  WorkflowPanel,
  ProductivityPanel,
  ActionsHistory,
  ChangeNotifications,
  InspectionMode,
  SyncStatusBanner,
  MindPanelHeader,
  WorkspacePanelHeader,
  FloatingConversationsButton,
};
